import React, { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import { motion, AnimatePresence, Transition } from 'framer-motion';
import { captureReferralCode } from './utils/referralUtils';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import DashboardLayout from './components/layouts/DashboardLayout';
import ImageUploader from './components/ImageUploader';
import ResultDisplay from './components/ResultDisplay';
import Loader from './components/Loader';
import LineLoader from './components/LineLoader';
import ImageCropper from './components/ImageCropper';
import OneToOneCropper from './components/OneToOneCropper';
import SmileStudio from './components/SmileStudio';
import MaskCreator from './components/MaskCreator';
import GumLineEditor from './components/GumLineEditor';
import MouthAnnotator from './components/MouthAnnotator';
import ClinicalGuideEditor from './components/ClinicalGuideEditor';
import HomePage from './components/HomePage';
import RefineScreen from './components/RefineScreen';
import RefineModal from './components/RefineModal';
import ValidationFeedbackModal, { ValidationChecks } from './components/ValidationFeedbackModal';
import GlobalAnnouncementBanner from './components/GlobalAnnouncementBanner';
import { generateSmileDesign, generateSmileDesignV2, generateSmileVideo, generateSmileAnalysis, generateRestorativeAnalysis, generateSmileRating, generateDentalModel, generateShowcasePortrait, upscaleAndEnhanceImage, generateAIDentistAnalysis, ValidationError } from './services/aiService';
import { toBase64, dataUrlToParts, padImageToRatio, drawImageWithOverlay, drawClinicalGuides, drawMouthAnnotation } from './utils/fileUtils';
import AlertTriangleIcon from './components/icons/AlertTriangleIcon';
import { AIDentistAnalysis } from './types';
import { notificationService } from './services/notificationService';

interface OriginalImageState {
  file: File;
  url: string;
}

export interface ClinicalGuides {
  midline: { start: { x: number; y: number }; end: { x: number; y: number } } | null;
  interpupillary: { start: { x: number; y: number }; end: { x: number; y: number } } | null;
  smileArc: { p1: { x: number; y: number }; p2: { x: number; y: number }; p3: { x: number; y: number } } | null;
}

export interface GenerationResult {
  baseImage: OriginalImageState;
  imageUrls: string[];
  texts: (string | null)[];
  treatmentPlan: object | null;
  smileRating: object | null;
  afterSmileRatings: (object | null)[];
  aiDentistAnalysis: AIDentistAnalysis | null;
  selectedStyles: string[];
  generationMode: GenerationMode;
  treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro';
}

export type GenerationMode = 'per-style' | 'variations';

// V2 Output Mode type
export type V2OutputMode = 'unique' | 'creatives';

// Model Version type
export type ModelVersion = 'v1' | 'v2';

// Lazy load heavy pages for code splitting (reduces initial bundle size by ~30%)
const BillingPage = lazy(() => import('./pages/dashboard/BillingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AdminCreditPanel = lazy(() => import('./pages/AdminCreditPanel'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const BlogHub = lazy(() => import('./pages/blog/index'));
const BlogPost = lazy(() => import('./pages/blog/[slug]'));
const DigitalSmileDesignPage = lazy(() => import('./pages/features/DigitalSmileDesignAI'));
const AutomatedSmileDesignPage = lazy(() => import('./pages/features/AutomatedSmileDesignPage'));
const AiInDigitalDentistryPage = lazy(() => import('./pages/solutions/AiInDigitalDentistryPage'));
const VirtualSmileMakeoverPage = lazy(() => import('./pages/solutions/VirtualSmileMakeoverPage'));
const CosmeticDentistryDigitalToolsPage = lazy(() => import('./pages/solutions/CosmeticDentistryDigitalToolsPage'));
const AiDentalImagingAnalysisPage = lazy(() => import('./pages/solutions/AiDentalImagingAnalysisPage'));
const SmileSimulationTechnologyPage = lazy(() => import('./pages/solutions/SmileSimulationTechnologyPage'));
const AiPoweredSmileDesignPage = lazy(() => import('./pages/features/AiPoweredSmileDesignPage'));
const AutomaticSmileDesignFromPhotoPage = lazy(() => import('./pages/features/AutomaticSmileDesignFromPhotoPage'));
const SmileDesignFacialAnalysisPage = lazy(() => import('./pages/features/SmileDesignFacialAnalysisPage'));
const AiEstheticDentalPlanningPage = lazy(() => import('./pages/features/AiEstheticDentalPlanningPage'));
const RealTimeSmilePreviewPage = lazy(() => import('./pages/features/RealTimeSmilePreviewPage'));
const BeforeAfterSmileSimulationPage = lazy(() => import('./pages/features/BeforeAfterSmileSimulationPage'));
const AiSmileMockupsForDentistsPage = lazy(() => import('./pages/features/AiSmileMockupsForDentistsPage'));
const SmileDesignWithoutManualDrawingPage = lazy(() => import('./pages/features/SmileDesignWithoutManualDrawingPage'));
const CloudBasedSmileDesignPage = lazy(() => import('./pages/features/CloudBasedSmileDesignPage'));
const BrowserBasedSmileDesignPage = lazy(() => import('./pages/features/BrowserBasedSmileDesignPage'));
// Use-case pages
const SmileDesignCosmeticDentistsPage = lazy(() => import('./pages/use-cases/SmileDesignCosmeticDentistsPage'));
const SmileDesignPrivateClinicsPage = lazy(() => import('./pages/use-cases/SmileDesignPrivateClinicsPage'));
const SmileDesignDailyConsultationsPage = lazy(() => import('./pages/use-cases/SmileDesignDailyConsultationsPage'));
const ChairsideSmileSimulationPage = lazy(() => import('./pages/use-cases/ChairsideSmileSimulationPage'));
const SmileDesignVeneersPage = lazy(() => import('./pages/use-cases/SmileDesignVeneersPage'));
const SmileDesignFullMouthRehabPage = lazy(() => import('./pages/use-cases/SmileDesignFullMouthRehabPage'));
const SmileDesignMinimallyInvasivePage = lazy(() => import('./pages/use-cases/SmileDesignMinimallyInvasivePage'));
const SmileDesignOrthodontistsPage = lazy(() => import('./pages/use-cases/SmileDesignOrthodontistsPage'));
const SmileDesignProsthodonticsPage = lazy(() => import('./pages/use-cases/SmileDesignProsthodonticsPage'));
const SmileDesignImplantCasesPage = lazy(() => import('./pages/use-cases/SmileDesignImplantCasesPage'));
const SmileDesignEstheticRestorationsPage = lazy(() => import('./pages/use-cases/SmileDesignEstheticRestorationsPage'));
const DigitalWaxUpAlternativePage = lazy(() => import('./pages/use-cases/DigitalWaxUpAlternativePage'));

// Guide pages
const ReduceSmileDesignTimePage = lazy(() => import('./pages/guides/ReduceSmileDesignTimePage'));
const AutomateDigitalSmileDesignPage = lazy(() => import('./pages/guides/AutomateDigitalSmileDesignPage'));
const AlternativesManualSmileDesignPage = lazy(() => import('./pages/guides/AlternativesManualSmileDesignPage'));
const TraditionalSmileDesignProblemsPage = lazy(() => import('./pages/guides/TraditionalSmileDesignProblemsPage'));
const ShowPatientsRealisticSmilePreviewsPage = lazy(() => import('./pages/guides/ShowPatientsRealisticSmilePreviewsPage'));
const ImprovePatientAcceptanceSimulationPage = lazy(() => import('./pages/guides/ImprovePatientAcceptanceSimulationPage'));
const StandardizeEstheticDentalPlanningPage = lazy(() => import('./pages/guides/StandardizeEstheticDentalPlanningPage'));
const AvoidSubjectiveSmileDesignDecisionsPage = lazy(() => import('./pages/guides/AvoidSubjectiveSmileDesignDecisionsPage'));
const AiVsTraditionalSmileDesignPage = lazy(() => import('./pages/guides/AiVsTraditionalSmileDesignPage'));
const ManualVsAutomaticSmileDesignPage = lazy(() => import('./pages/guides/ManualVsAutomaticSmileDesignPage'));
const DigitalWaxUpVsAiSimulationPage = lazy(() => import('./pages/guides/DigitalWaxUpVsAiSimulationPage'));
const SmileDesignSoftwareVsPhotoshopPage = lazy(() => import('./pages/guides/SmileDesignSoftwareVsPhotoshopPage'));
const ChairsideVsLabPlanningPage = lazy(() => import('./pages/guides/ChairsideVsLabPlanningPage'));
const CloudVsDesktopSmileDesignPage = lazy(() => import('./pages/guides/CloudVsDesktopSmileDesignPage'));

// Showcase Creator (free tool)
const ShowcaseCreatorPage = lazy(() => import('./pages/ShowcaseCreatorPage'));


// Auth pages loaded eagerly since they're the first interaction point
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailure from './pages/PaymentFailure';

import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PatientStorageService } from './services/patientStorageService';
import ErrorBoundary from './components/ErrorBoundary';
import { useCreditCheck } from './hooks/useCreditCheck';
import CreditPurchaseModal from './components/CreditPurchaseModal';

import MfaSetupPage from './pages/MfaSetupPage';

// Lazy loading fallback component
const LazyLoadFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-zinc-100 dark:bg-zinc-950">
    <div className="flex flex-col items-center space-y-4">
      <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
      <p className="text-zinc-600 dark:text-zinc-400">Loading...</p>
    </div>
  </div>
);

const PrivateRoute: React.FC = () => {
  const { user, isLoading, isInitialized } = useAuth();
  const location = useLocation();

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100 dark:bg-zinc-950">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-zinc-600 dark:text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <Outlet /> : <Navigate to="/login" state={{ from: location }} replace />;
}

// Wrapper components for Guide and Pricing pages to include Header
const GuidePageWrapper: React.FC = () => {
  const navigate = useNavigate();
  const handleLaunchApp = () => navigate('/app');
  const handleGoHome = () => navigate('/');


  return (
    <div className="h-screen w-full bg-zinc-100 dark:bg-zinc-950 overflow-hidden">
      <div className="flex flex-col h-full overflow-auto">
        <Header appState="site" onLaunchApp={handleLaunchApp} onGoHome={handleGoHome} />
        <main className="flex-grow w-full">
          <GuidePage />
        </main>
      </div>
    </div>
  );
};

const PricingPageWrapper: React.FC = () => {
  const navigate = useNavigate();
  const handleLaunchApp = () => navigate('/app');
  const handleGoHome = () => navigate('/');


  return (
    <div className="h-screen w-full bg-zinc-100 dark:bg-zinc-950 overflow-hidden">
      <div className="flex flex-col h-full overflow-auto">
        <Header appState="site" onLaunchApp={handleLaunchApp} onGoHome={handleGoHome} />
        <main className="flex-grow w-full">
          <PricingPage />
        </main>
      </div>
    </div>
  );
};

const AboutUsPageWrapper: React.FC = () => {
  const navigate = useNavigate();
  const handleLaunchApp = () => navigate('/app');
  const handleGoHome = () => navigate('/');


  return (
    <div className="h-screen w-full bg-zinc-100 dark:bg-zinc-950 overflow-hidden">
      <div className="flex flex-col h-full overflow-auto">
        <Header appState="site" onLaunchApp={handleLaunchApp} onGoHome={handleGoHome} />
        <main className="flex-grow w-full">
          <AboutUsPage />
        </main>
      </div>
    </div>
  );
};

const FeaturePageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const handleLaunchApp = () => navigate('/app');
  const handleGoHome = () => navigate('/');

  return (
    <div className="h-screen w-full bg-zinc-100 dark:bg-zinc-950 overflow-hidden">
      <div className="flex flex-col h-full overflow-auto">
        <Header appState="site" onLaunchApp={handleLaunchApp} onGoHome={handleGoHome} />
        <main className="flex-grow w-full">
          {children}
        </main>
      </div>
    </div>
  );
};

const SolutionsPageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const handleLaunchApp = () => navigate('/app');
  const handleGoHome = () => navigate('/');

  return (
    <div className="h-screen w-full bg-zinc-100 dark:bg-zinc-950 overflow-hidden">
      <div className="flex flex-col h-full overflow-auto">
        <Header appState="site" onLaunchApp={handleLaunchApp} onGoHome={handleGoHome} />
        <main className="flex-grow w-full">
          {children}
        </main>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const { refreshCredits, user } = useAuth();

  // Dynamic credit check hooks (will check based on calculated image count)
  const [showDesignPurchaseModal, setShowDesignPurchaseModal] = useState(false);
  const [showVideoPurchaseModal, setShowVideoPurchaseModal] = useState(false);
  const [showPortraitPurchaseModal, setShowPortraitPurchaseModal] = useState(false);
  const [showModelPurchaseModal, setShowModelPurchaseModal] = useState(false);

  const {
    checkCredits: checkVideoCredits
  } = useCreditCheck(2);

  const {
    checkCredits: checkPortraitCredits
  } = useCreditCheck(1);

  const {
    checkCredits: checkModelCredits
  } = useCreditCheck(1);

  const [appState, setAppState] = useState<'site' | 'app'>('site');
  const [originalImage, setOriginalImage] = useState<OriginalImageState | null>(null);

  // New state for multiple generation results
  const [generationResults, setGenerationResults] = useState<GenerationResult[]>([]);
  const [activeGenerationIndex, setActiveGenerationIndex] = useState(0);
  const [activeResultIndex, setActiveResultIndex] = useState(0);

  // Patient name state for saving cases
  const [patientName, setPatientName] = useState<string>('');
  const [currentCaseId, setCurrentCaseId] = useState<number | undefined>(undefined);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [selectedStyles, setSelectedStyles] = useState<string[]>(['Natural Enhanced']);
  const [generationMode, setGenerationMode] = useState<GenerationMode>('per-style');
  const [treatmentType, setTreatmentType] = useState<'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro'>('cosmetic');
  const [isAutomaticPlanning, setIsAutomaticPlanning] = useState<boolean>(true);
  const [selectedOrthoStyle, setSelectedOrthoStyle] = useState<string>('Final Aligned Result');

  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);

  const [extractedDentureUrl, setExtractedDentureUrl] = useState<string | null>(null);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [isCroppingDenture, setIsCroppingDenture] = useState<boolean>(false);
  const [isExtractionViewerVisible, setIsExtractionViewerVisible] = useState(false);

  const [patientPortraitUrl, setPatientPortraitUrl] = useState<string | null>(null);
  const [portraitError, setPortraitError] = useState<string | null>(null);

  const [isGumLineEditorOpen, setIsGumLineEditorOpen] = useState<boolean>(false);
  const [gumLinePath, setGumLinePath] = useState<{ x: number; y: number }[] | null>(null);

  const [isClinicalGuideEditorOpen, setIsClinicalGuideEditorOpen] = useState<boolean>(false);
  const [clinicalGuides, setClinicalGuides] = useState<ClinicalGuides | null>(null);

  const [isUpscaled, setIsUpscaled] = useState<boolean>(false);
  const [isCroppingOneToOne, setIsCroppingOneToOne] = useState<boolean>(false);

  const [isMouthAnnotatorOpen, setIsMouthAnnotatorOpen] = useState<boolean>(false);
  const [mouthPath, setMouthPath] = useState<{ x: number; y: number }[] | null>(null);

  const [isAIDentistEnabled, setIsAIDentistEnabled] = useState<boolean>(false);

  // V2 Model State
  const [modelVersion, setModelVersion] = useState<ModelVersion>('v2');
  const [v2OutputMode, setV2OutputMode] = useState<V2OutputMode>('unique');
  const [v2CosmeticStyle, setV2CosmeticStyle] = useState<'natural' | 'hollywood'>('natural');

  const [isStudioOpen, setIsStudioOpen] = useState<boolean>(false);
  const [studioFaceImageUrl, setStudioFaceImageUrl] = useState<string | null>(null);
  const [isCreatingMask, setIsCreatingMask] = useState<boolean>(false);

  // New state for iterative design modal
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [isRefining, setIsRefining] = useState(false); // For line loader

  // Validation UI State
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationChecks, setValidationChecks] = useState<ValidationChecks | null>(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // This effect will run when the user logs in or out, or when the location changes.
    // It helps synchronize the appState with the routing.
    if (location.pathname.startsWith('/app') || location.pathname === '/dashboard') {
      setAppState('app');
    } else {
      setAppState('site');
    }
  }, [location.pathname, user]);

  // Capture referral codes from URL on any page load
  useEffect(() => {
    captureReferralCode();
  }, [location]);

  const handleLaunchApp = useCallback(() => {
    navigate('/app');
    handleReset();
  }, [navigate]);


  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.documentElement.classList.remove('light');
    localStorage.setItem('theme', 'dark');
  }, []);



  // Connect to SSE when user is authenticated
  useEffect(() => {
    if (user) {
      notificationService.connect();
    } else {
      notificationService.disconnect();
    }
    return () => notificationService.disconnect();
  }, [user]);

  // Subscribe to progress updates
  useEffect(() => {
    const unsubscribe = notificationService.subscribe((event) => {
      if (event.type === 'progress' && event.message) {
        setLoadingMessage(event.message);
      }
    });
    return unsubscribe;
  }, []);

  const handleImageUpload = (file: File) => {
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        if (originalImage?.url) URL.revokeObjectURL(originalImage.url);
        const isOneToOne = img.naturalWidth === img.naturalHeight;
        setOriginalImage({ file, url: imageUrl });
        handleReset(false); // Reset without clearing original image
        if (!isOneToOne) setIsCroppingOneToOne(true);
        else setIsMouthAnnotatorOpen(true);
      };
      img.onerror = () => { setError("Could not load image."); URL.revokeObjectURL(imageUrl); };
      img.src = imageUrl;
    }
  };

  const handleConfirmCrop = (croppedFile: File) => {
    if (originalImage) URL.revokeObjectURL(originalImage.url);
    setOriginalImage({ file: croppedFile, url: URL.createObjectURL(croppedFile) });
    setIsUpscaled(false);
    setIsCroppingOneToOne(false);
    setIsMouthAnnotatorOpen(true);
  };

  const generateAndSetResults = async (
    baseImage: OriginalImageState,
    currentTreatmentType: typeof treatmentType,
    currentAutomaticPlanning: boolean,
    currentSelectedStyles: string[],
    currentCustomPrompt: string,
    currentAIDentistEnabled: boolean,
    currentGenerationMode: GenerationMode,
    isInitialGeneration: boolean,
  ) => {
    if (!mouthPath) {
      setError("Please define the mouth area before generating a new smile.");
      return;
    }

    const loaderSetter = isInitialGeneration ? setIsLoading : setIsRefining;
    loaderSetter(true);
    setLoadingMessage(undefined);
    setError(null);

    if (isInitialGeneration) {
      setGenerationResults([]);
      setActiveGenerationIndex(0);
      setActiveResultIndex(0);
    }

    // Reset volatile results for a new generation
    setGeneratedVideoUrl(null);
    setVideoError(null);
    setPatientPortraitUrl(null);
    setPortraitError(null);
    setExtractedDentureUrl(null);
    setExtractionError(null);

    try {
      let compositeImageUrl = baseImage.url;
      if (mouthPath && mouthPath.length > 0) { setLoadingMessage("Applying mouth annotation..."); compositeImageUrl = await drawMouthAnnotation(compositeImageUrl, mouthPath); }
      if (gumLinePath && gumLinePath.length > 0) { setLoadingMessage("Applying gum line..."); compositeImageUrl = await drawImageWithOverlay(compositeImageUrl, gumLinePath); }
      if (clinicalGuides) { setLoadingMessage("Applying clinical guides..."); compositeImageUrl = await drawClinicalGuides(compositeImageUrl, clinicalGuides); }

      const { base64: finalBase64, mimeType: finalMimeType } = dataUrlToParts(compositeImageUrl) ?? { base64: await toBase64(baseImage.file), mimeType: baseImage.file.type };
      // true original (pre-annotation) base64
      const originalBase64 = await toBase64(baseImage.file);
      const analysisBase64 = originalBase64;
      const analysisMimeType = baseImage.file.type;

      // 1. Start Analysis in Background (Don't await yet)
      const smileRatingPromise = generateSmileRating(analysisBase64, analysisMimeType, currentCaseId);
      const aiDentistPromise = currentAIDentistEnabled ? generateAIDentistAnalysis(analysisBase64, analysisMimeType) : Promise.resolve(null);

      let treatmentPlanJson = '';
      let treatmentPlan: object | null = null;
      if (currentTreatmentType === 'cosmetic' && currentAutomaticPlanning) {
        setLoadingMessage("Performing smile analysis...");
        treatmentPlan = await generateSmileAnalysis(analysisBase64, analysisMimeType, currentCaseId);
        treatmentPlanJson = JSON.stringify(treatmentPlan, null, 2);
      } else if (currentTreatmentType === 'restorative') {
        setLoadingMessage("Performing restorative analysis...");
        treatmentPlan = await generateRestorativeAnalysis(analysisBase64, analysisMimeType, currentCaseId);
        treatmentPlanJson = JSON.stringify(treatmentPlan, null, 2);
      }

      let numVisions = (currentGenerationMode === 'variations' && currentTreatmentType !== 'pro-automated') ? 3 : (currentTreatmentType === 'cosmetic' && !currentAutomaticPlanning && currentGenerationMode === 'per-style') ? currentSelectedStyles.length : (currentTreatmentType === 'pro-automated') ? 2 : 1;
      setLoadingMessage(numVisions > 1 ? `Generating ${numVisions} visions...` : "Generating simulation...");

      // 2. Generate Images (Blocking)
      const { images, caseId: returnedCaseId } = await generateSmileDesign(
        finalBase64,
        finalMimeType,
        currentCustomPrompt,
        currentSelectedStyles,
        currentGenerationMode,
        currentTreatmentType,
        treatmentPlanJson,
        gumLinePath,
        clinicalGuides,
        currentCaseId,
        patientName || `Case ${new Date().toLocaleDateString()}`,
        `Notes: Generated on ${new Date().toLocaleString()}`,
        { treatmentType: currentTreatmentType, selectedStyles: currentSelectedStyles, generationMode: currentGenerationMode, customPrompt: currentCustomPrompt, patientName },
        originalBase64,
        setLoadingMessage // Pass progress callback for queue updates
      );

      // Update currentCaseId if a new case was created
      if (returnedCaseId && !currentCaseId) {
        setCurrentCaseId(returnedCaseId);
      }
      const imageUrls = images.map(img => `data:${img.imageData.startsWith('data:') ? '' : `${finalMimeType};base64,`}${img.imageData}`);
      const texts = images.map(img => img.textResponse);

      // 3. IMMEDIATE UPDATE: Show images with placeholder analysis
      const partialResult: GenerationResult = {
        baseImage, imageUrls, texts, treatmentPlan,
        smileRating: null, // Placeholder
        afterSmileRatings: Array(imageUrls.length).fill(null), // Placeholder
        aiDentistAnalysis: null, // Placeholder
        selectedStyles: currentSelectedStyles, generationMode: currentGenerationMode, treatmentType: currentTreatmentType,
      };

      setGenerationResults(prev => [...prev, partialResult]);
      setActiveGenerationIndex(isInitialGeneration ? 0 : generationResults.length);
      setActiveResultIndex(0);

      // Stop the main loader here so user sees images
      loaderSetter(false);
      setLoadingMessage(undefined);

      // 4. Continue Analysis in Background
      const afterRatingsPromises = imageUrls.map(url => generateSmileRating(dataUrlToParts(url)!.base64, dataUrlToParts(url)!.mimeType, currentCaseId || returnedCaseId).catch(() => null));

      Promise.all([smileRatingPromise, aiDentistPromise, Promise.all(afterRatingsPromises)])
        .then(([smileRating, aiDentistAnalysis, afterSmileRatings]) => {
          // 5. UPDATE WITH FULL DATA
          setGenerationResults(prev => {
            const newResults = [...prev];
            // Find the result we just added by matching the first image URL
            const targetIndex = newResults.findIndex(r => r.imageUrls[0] === imageUrls[0]);
            if (targetIndex !== -1) {
              newResults[targetIndex] = {
                ...newResults[targetIndex],
                smileRating,
                aiDentistAnalysis,
                afterSmileRatings
              };
            }
            return newResults;
          });

          // Refresh credits after successful generation
          refreshCredits();
          const imageCount = imageUrls.length;
          toast.success(`Analysis complete!`, {
            duration: 3000,
            icon: '✅',
          });
        })
        .catch(err => {
          console.error("Background analysis failed", err);
          toast.error("Some analysis data could not be loaded.");
        });

      // Note: We moved refreshCredits and toast to the background completion or here?
      // Credits are deducted on server start, so we just refresh balance.
      // We can refresh balance now too just in case.
      refreshCredits();

    } catch (err) {
      console.error(err);
      if (err instanceof ValidationError) {
        setValidationErrors(err.errors);
        setValidationChecks(err.checks);
        setIsValidationModalOpen(true);
      } else {
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      }
      loaderSetter(false);
      setLoadingMessage(undefined);
    }
  };

  const generateAndSetResultsV2 = async (
    baseImage: OriginalImageState,
    currentTreatmentType: 'cosmetic' | 'orthodontic' | 'restorative',
    currentOutputMode: V2OutputMode,
    currentCosmeticStyle: 'natural' | 'hollywood',
    isInitialGeneration: boolean
  ) => {
    if (!mouthPath) {
      setError("Please define the mouth area before generating a new smile.");
      return;
    }

    const loaderSetter = isInitialGeneration ? setIsLoading : setIsRefining;
    loaderSetter(true);
    setLoadingMessage(undefined);
    setError(null);

    if (isInitialGeneration) {
      setGenerationResults([]);
      setActiveGenerationIndex(0);
      setActiveResultIndex(0);
    }

    // Reset volatile results
    setGeneratedVideoUrl(null);
    setVideoError(null);
    setPatientPortraitUrl(null);
    setPortraitError(null);
    setExtractedDentureUrl(null);
    setExtractionError(null);

    try {
      // 1. Prepare Image with Visual Guides
      let compositeImageUrl = baseImage.url;

      // Apply Mouth Annotation (Visual Guide) - SKIPPED FOR V2
      // V2 must NOT have the mouth mask (green area) burnt into the image.
      // It only supports Gum Line guide.
      /* 
      if (mouthPath && mouthPath.length > 0) {
        setLoadingMessage("Applying mouth annotation...");
        compositeImageUrl = await drawMouthAnnotation(compositeImageUrl, mouthPath);
      }
      */

      // Apply Gum Line (Visual Guide)
      if (gumLinePath && gumLinePath.length > 0) {
        setLoadingMessage("Applying gum line...");
        compositeImageUrl = await drawImageWithOverlay(compositeImageUrl, gumLinePath);
      }

      // Note: Clinical Guides are V1 only, skipped for V2.

      const { base64: finalBase64, mimeType: finalMimeType } = dataUrlToParts(compositeImageUrl) ?? { base64: await toBase64(baseImage.file), mimeType: baseImage.file.type };
      const originalBase64 = await toBase64(baseImage.file);

      // 1.5 Start Background Analysis (Smile Rating Only for V2)
      // Use original image for "Before" score
      const smileRatingPromise = generateSmileRating(originalBase64, baseImage.file.type, currentCaseId);

      // 2. Generate Images (V2 Engine)
      const numVisions = currentOutputMode === 'creatives' ? 3 : 1;
      setLoadingMessage(numVisions > 1 ? `Generating ${numVisions} creative visions (V2)...` : "Generating V2 simulation...");

      // Determine guide presence flags
      const hasMouthGuide = !!(mouthPath && mouthPath.length > 0);
      const hasGumLineGuide = !!(gumLinePath && gumLinePath.length > 0);

      const { images, caseId: returnedCaseId } = await generateSmileDesignV2(
        finalBase64,
        finalMimeType,
        currentTreatmentType,
        currentOutputMode,
        hasMouthGuide,
        hasGumLineGuide,
        currentCaseId,
        patientName || `Case ${new Date().toLocaleDateString()}`,
        `Notes: V2 Generated on ${new Date().toLocaleString()}`,
        { treatmentType: currentTreatmentType, outputMode: currentOutputMode, version: 'v2', cosmeticStyle: currentCosmeticStyle },
        originalBase64,
        currentCosmeticStyle,
        setLoadingMessage // Pass progress callback for queue updates
      );

      // Update currentCaseId if a new case was created
      if (returnedCaseId && !currentCaseId) {
        setCurrentCaseId(returnedCaseId);
      }

      const imageUrls = images.map(img => `data:${img.imageData.startsWith('data:') ? '' : `${finalMimeType};base64,`}${img.imageData}`);
      const texts = images.map(img => img.textResponse);

      // 3. Update State
      const partialResult: GenerationResult = {
        baseImage,
        imageUrls,
        texts,
        treatmentPlan: null, // No V1 plan
        smileRating: null,   // Placeholder, will update later
        afterSmileRatings: Array(imageUrls.length).fill(null), // Placeholder
        aiDentistAnalysis: null, // No V1 AI dentist
        selectedStyles: [], // V2 has no explicit styles
        // Map V2 mode to closest V1 generation mode for type compatibility
        generationMode: currentOutputMode === 'creatives' ? 'variations' : 'per-style',
        treatmentType: currentTreatmentType,
      };

      setGenerationResults(prev => [...prev, partialResult]);
      setActiveGenerationIndex(isInitialGeneration ? 0 : generationResults.length);
      setActiveResultIndex(0);

      // Stop loader so user sees images immediately
      loaderSetter(false);
      setLoadingMessage(undefined);

      // 4. Continue Analysis in Background (After Scores)
      const afterRatingsPromises = imageUrls.map(url => generateSmileRating(dataUrlToParts(url)!.base64, dataUrlToParts(url)!.mimeType, currentCaseId || returnedCaseId).catch(() => null));

      Promise.all([smileRatingPromise, Promise.all(afterRatingsPromises)])
        .then(([smileRating, afterSmileRatings]) => {
          // 5. UPDATE WITH SCORES
          setGenerationResults(prev => {
            const newResults = [...prev];
            const targetIndex = newResults.findIndex(r => r.imageUrls[0] === imageUrls[0]);
            if (targetIndex !== -1) {
              newResults[targetIndex] = {
                ...newResults[targetIndex],
                smileRating,
                afterSmileRatings
              };
            }
            return newResults;
          });

          // Refresh credits after successful generation
          refreshCredits();
          toast.success(`Analysis complete!`, {
            duration: 3000,
            icon: '✅',
          });
        })
        .catch(err => {
          console.error("Background analysis failed", err);
          toast.error("Smile scoring could not be loaded.");
        });

    } catch (err) {
      console.error(err);
      if (err instanceof ValidationError) {
        setValidationErrors(err.errors);
        setValidationChecks(err.checks);
        setIsValidationModalOpen(true);
      } else {
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      }
      loaderSetter(false);
      setLoadingMessage(undefined);
    }
  };

  // Calculate how many images will be generated (1 credit per image)
  const calculateImageCount = useCallback((
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro',
    isAutomaticPlanning: boolean,
    generationMode: GenerationMode,
    selectedStyles: string[]
  ): number => {
    if (generationMode === 'variations' && treatmentType !== 'pro-automated') {
      return 3; // variations mode generates 3 images
    } else if (treatmentType === 'cosmetic' && !isAutomaticPlanning && generationMode === 'per-style') {
      return selectedStyles.length; // one image per style
    } else if (treatmentType === 'pro-automated') {
      return 2; // pro-automated generates 2 images
    }
    return 1; // default
  }, []);

  const handleGenerate = useCallback(() => {
    if (!originalImage) return;

    // Calculate how many images will be generated
    const numImages = calculateImageCount(treatmentType, isAutomaticPlanning, generationMode, selectedStyles);

    const currentCredits = user?.credits || 0;

    if (modelVersion === 'v2') {
      const v2Cost = v2OutputMode === 'creatives' ? 3 : 1;
      if (currentCredits < v2Cost) {
        const shortfall = v2Cost - currentCredits;
        toast.error(
          `Insufficient credits. You need ${v2Cost} credit${v2Cost > 1 ? 's' : ''} (${v2Cost} image${v2Cost > 1 ? 's' : ''}) but have ${currentCredits}. Need ${shortfall} more.`,
          { duration: 5000, icon: '💳' }
        );
        setShowDesignPurchaseModal(true);
        return;
      }
      generateAndSetResultsV2(originalImage, treatmentType as any, v2OutputMode, v2CosmeticStyle, true);
    } else {
      if (currentCredits < numImages) {
        const shortfall = numImages - currentCredits;
        toast.error(
          `Insufficient credits. You need ${numImages} credit${numImages > 1 ? 's' : ''} (${numImages} image${numImages > 1 ? 's' : ''}) but have ${currentCredits}. Need ${shortfall} more.`,
          { duration: 5000, icon: '💳' }
        );
        setShowDesignPurchaseModal(true);
        return;
      }
      generateAndSetResults(originalImage, treatmentType, isAutomaticPlanning, selectedStyles, customPrompt, isAIDentistEnabled, generationMode, true);
    }
  }, [originalImage, treatmentType, isAutomaticPlanning, selectedStyles, customPrompt, isAIDentistEnabled, generationMode, mouthPath, gumLinePath, clinicalGuides, calculateImageCount, user, modelVersion, v2OutputMode, v2CosmeticStyle, patientName]);

  const handleGenerateFromRefineModal = useCallback(async (config: {
    baseImageUrl: string;
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro';
    isAutomaticPlanning: boolean;
    selectedStyles: string[];
    customPrompt: string;
    isAIDentistEnabled: boolean;
    generationMode: GenerationMode;
  }) => {
    setIsRefineModalOpen(false);
    const { baseImageUrl, ...rest } = config;

    const response = await fetch(baseImageUrl);
    const blob = await response.blob();
    const file = new File([blob], `refined-base-${Date.now()}.jpg`, { type: blob.type });
    const newBaseImage = { file, url: baseImageUrl };

    generateAndSetResults(newBaseImage, rest.treatmentType, rest.isAutomaticPlanning, rest.selectedStyles, rest.customPrompt, rest.isAIDentistEnabled, rest.generationMode, false);
  }, [mouthPath, gumLinePath, clinicalGuides, generationResults.length, patientName]);

  const handleUpscaleImage = useCallback(async () => {
    if (!originalImage) return;
    setIsLoading(true); setLoadingMessage("Enhancing image quality..."); setError(null);
    try {
      const { base64, mimeType } = await upscaleAndEnhanceImage(await toBase64(originalImage.file), originalImage.file.type);
      const dataUrl = `data:${mimeType};base64,${base64}`;
      const newFile = new File([await (await fetch(dataUrl)).blob()], originalImage.file.name.replace(/(\.[\w\d_-]+)$/i, '-upscaled$1'), { type: mimeType });
      URL.revokeObjectURL(originalImage.url);
      setOriginalImage({ file: newFile, url: URL.createObjectURL(newFile) });
      setIsUpscaled(true);
    } catch (err) { setError(err instanceof Error ? err.message : 'Upscale failed.'); }
    finally { setIsLoading(false); setLoadingMessage(undefined); }
  }, [originalImage]);

  const currentGeneration = generationResults[activeGenerationIndex];
  const activeGeneratedUrl = currentGeneration?.imageUrls?.[activeResultIndex];


  const handleGenerateVideo = useCallback(async (aspectRatio: string = '16:9') => {
    if (!activeGeneratedUrl) return;

    // Check credits before video generation
    if (!checkVideoCredits()) {
      return;
    }

    setIsLoading(true); setLoadingMessage("Preparing animation..."); setVideoError(null); setGeneratedVideoUrl(null);
    try {
      const imageParts = dataUrlToParts(activeGeneratedUrl);
      if (!imageParts) throw new Error("Invalid image format.");
      const videoUrl = await generateSmileVideo(imageParts.base64, imageParts.mimeType, setLoadingMessage, aspectRatio, currentCaseId);
      setGeneratedVideoUrl(videoUrl);

      // Note: Video artifact is already saved to GCS by the backend during generation
      // No need to call saveArtifact here - it would create a duplicate DB-only record

      // Refresh credits after successful generation
      await refreshCredits();
      toast.success('Video generated! 2 credits used.');
    } catch (err) { setVideoError(err instanceof Error ? err.message : 'Video generation failed.'); }
    finally { setIsLoading(false); setLoadingMessage(undefined); }
  }, [activeGeneratedUrl, checkVideoCredits, refreshCredits, currentCaseId]);

  const handleGenerateDentalModel = useCallback(async (croppedImageBase64: string) => {
    // Check credits before dental model generation
    if (!checkModelCredits()) {
      setIsCroppingDenture(false);
      return;
    }

    setIsCroppingDenture(false); setIsLoading(true); setLoadingMessage("Generating dental model..."); setExtractionError(null); setExtractedDentureUrl(null);
    try {
      const imageParts = dataUrlToParts(croppedImageBase64);
      if (!imageParts) throw new Error("Invalid cropped image format.");
      const extractedBase64 = await generateDentalModel(imageParts.base64, imageParts.mimeType, setLoadingMessage);
      const dataUrl = `data:image/png;base64,${extractedBase64}`;
      setExtractedDentureUrl(dataUrl);
      setIsExtractionViewerVisible(true);

      // Save artifact if caseId exists
      if (currentCaseId) {
        import('./services/casesApiClient').then(({ saveArtifact }) => {
          saveArtifact(currentCaseId, {
            artifactType: 'dental_model',
            content: extractedBase64,
            metadata: { mimeType: 'image/png' }
          }).catch(e => console.error("Failed to save dental model artifact:", e));
        });
      }

      // Refresh credits after successful generation
      await refreshCredits();
      toast.success('Dental model generated! 1 credit used.');
    } catch (err) { setExtractionError(err instanceof Error ? err.message : 'Model generation failed.'); }
    finally { setIsLoading(false); setLoadingMessage(undefined); }
  }, [checkModelCredits, refreshCredits, currentCaseId]);

  const handleGenerateShowcasePortrait = useCallback(async () => {
    if (!activeGeneratedUrl) return;

    // Check credits before portrait generation
    if (!checkPortraitCredits()) {
      return;
    }

    setIsLoading(true); setLoadingMessage("Generating portrait..."); setPortraitError(null); setPatientPortraitUrl(null);
    try {
      const imageParts = dataUrlToParts(activeGeneratedUrl);
      if (!imageParts) throw new Error("Invalid image format.");
      const { base64, mimeType } = await generateShowcasePortrait(imageParts.base64, imageParts.mimeType, setLoadingMessage);
      const dataUrl = `data:${mimeType};base64,${base64}`;
      setPatientPortraitUrl(dataUrl);

      // Save artifact if caseId exists
      if (currentCaseId) {
        import('./services/casesApiClient').then(({ saveArtifact }) => {
          saveArtifact(currentCaseId, {
            artifactType: 'patient_portrait',
            content: base64,
            metadata: { mimeType }
          }).catch(e => console.error("Failed to save portrait artifact:", e));
        });
      }

      // Refresh credits after successful generation
      await refreshCredits();
      toast.success('Portrait generated! 1 credit used.');
    } catch (err) { setPortraitError(err instanceof Error ? err.message : 'Portrait generation failed.'); }
    finally { setIsLoading(false); setLoadingMessage(undefined); }
  }, [activeGeneratedUrl, checkPortraitCredits, refreshCredits, currentCaseId]);


  const handleReset = (fullReset = true) => {
    if (fullReset && originalImage) URL.revokeObjectURL(originalImage.url);
    if (fullReset) setOriginalImage(null);
    setGenerationResults([]);
    setError(null);
    setIsLoading(false);
    setCustomPrompt('');
    setSelectedStyles(['Natural Enhanced']);
    setGenerationMode('per-style');
    setTreatmentType('cosmetic');
    setSelectedOrthoStyle('Final Aligned Result');
    setGeneratedVideoUrl(null);
    setVideoError(null);
    setLoadingMessage(undefined);
    setExtractedDentureUrl(null);
    setExtractionError(null);
    setIsCroppingDenture(false);
    setIsExtractionViewerVisible(false);
    setPatientPortraitUrl(null);
    setPortraitError(null);
    setIsStudioOpen(false);
    setStudioFaceImageUrl(null);
    setIsCreatingMask(false);
    setGumLinePath(null);
    setIsGumLineEditorOpen(false);
    setMouthPath(null);
    setIsMouthAnnotatorOpen(false);
    setClinicalGuides(null);
    setIsClinicalGuideEditorOpen(false);
    setIsUpscaled(false);
    setActiveGenerationIndex(0);
    setActiveResultIndex(0);
    setIsAIDentistEnabled(false);
    setPatientName('');
    setCurrentCaseId(undefined);
  };

  const handleGoBackToRefine = () => {
    setGenerationResults([]); // Back to refine screen means clearing results
    // Keep originalImage, mouthPath, etc. for re-generation
  };

  const handleStartMaskCreation = useCallback(() => {
    if (!originalImage || !extractedDentureUrl) return;
    setIsCreatingMask(true);
  }, [originalImage, extractedDentureUrl]);

  const handleMaskCreationComplete = (maskedImageDataUrl: string) => {
    setIsCreatingMask(false);
    setStudioFaceImageUrl(maskedImageDataUrl);
    setIsStudioOpen(true);
  };

  const pageVariants = {
    initial: { opacity: 0, scale: 0.98 },
    in: { opacity: 1, scale: 1 },
    out: { opacity: 0, scale: 0.98 },
  };
  const pageTransition: Transition = { type: 'tween', ease: "easeInOut", duration: 0.4 };

  const renderAppContent = () => {
    if (error) {
      return (
        <motion.div key="error" variants={pageVariants} initial="initial" animate="in" exit="out" transition={pageTransition} className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
          <div className="bg-red-500/10 border border-red-500/20 p-8 rounded-2xl max-w-lg shadow-lg shadow-red-950/20" role="alert">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/20"><AlertTriangleIcon className="w-6 h-6 text-red-500" /></div>
            </div>
            <p className="font-bold text-2xl text-red-400">An Error Occurred</p>
            <p className="text-zinc-400 mt-2">{error}</p>
          </div>
        </motion.div>
      );
    }

    if (generationResults.length > 0 && currentGeneration) {
      return (
        <motion.div key="result" variants={pageVariants} initial="initial" animate="in" exit="out" transition={pageTransition} className="w-full h-full">
          <ResultDisplay
            generationResults={generationResults}
            activeGenerationIndex={activeGenerationIndex}
            onActiveGenerationChange={setActiveGenerationIndex}
            activeResultIndex={activeResultIndex}
            onActiveResultChange={setActiveResultIndex}
            onReset={() => handleReset(true)}
            onGoBack={handleGoBackToRefine}
            onGenerateVideo={handleGenerateVideo}
            generatedVideoUrl={generatedVideoUrl}
            videoError={videoError}
            onClearVideo={() => setGeneratedVideoUrl(null)}
            onStartDentureCrop={() => setIsCroppingDenture(true)}
            extractedDentureUrl={extractedDentureUrl}
            extractionError={extractionError}
            onClearExtractedDenture={() => { setExtractedDentureUrl(null); setExtractionError(null); setIsExtractionViewerVisible(false); }}
            onStartMaskCreation={handleStartMaskCreation}
            isExtractionViewerVisible={isExtractionViewerVisible}
            onCloseExtractionViewer={() => setIsExtractionViewerVisible(false)}
            onOpenExtractionViewer={() => setIsExtractionViewerVisible(true)}
            onGenerateShowcasePortrait={handleGenerateShowcasePortrait}
            patientPortraitUrl={patientPortraitUrl}
            portraitError={portraitError}
            onOpenRefineModal={() => setIsRefineModalOpen(true)}
            currentCaseId={currentCaseId}
          />
        </motion.div>
      );
    }

    if (originalImage) {
      if (isCroppingOneToOne) {
        return (
          <OneToOneCropper
            imageUrl={originalImage.url}
            onCrop={handleConfirmCrop}
            onCancel={() => handleReset(true)}
          />
        );
      }
      if (isMouthAnnotatorOpen) {
        return (
          <MouthAnnotator
            imageUrl={originalImage.url}
            onConfirm={(path) => { setMouthPath(path); setIsMouthAnnotatorOpen(false); }}
            onCancel={() => setIsMouthAnnotatorOpen(false)} // Or maybe go back to cropper if that was the prev step
            initialPath={mouthPath}
          />
        );
      }
      return (
        <motion.div key="refine" variants={pageVariants} initial="initial" animate="in" exit="out" transition={pageTransition} className="w-full h-full">
          <RefineScreen
            originalImage={originalImage} isUpscaled={isUpscaled} handleUpscaleImage={handleUpscaleImage} setIsGumLineEditorOpen={setIsGumLineEditorOpen} gumLinePath={gumLinePath}
            setIsClinicalGuideEditorOpen={setIsClinicalGuideEditorOpen} clinicalGuides={clinicalGuides} setIsMouthAnnotatorOpen={setIsMouthAnnotatorOpen} mouthPath={mouthPath}
            setIsCroppingOneToOne={setIsCroppingOneToOne} treatmentType={treatmentType} setTreatmentType={setTreatmentType} isAutomaticPlanning={isAutomaticPlanning}
            setIsAutomaticPlanning={setIsAutomaticPlanning} selectedStyles={selectedStyles} setSelectedStyles={setSelectedStyles} selectedOrthoStyle={selectedOrthoStyle}
            setSelectedOrthoStyle={setSelectedOrthoStyle} customPrompt={customPrompt} setCustomPrompt={setCustomPrompt} handleGenerate={handleGenerate} isLoading={isLoading}
            handleReset={() => handleReset(true)} isAIDentistEnabled={isAIDentistEnabled} setIsAIDentistEnabled={setIsAIDentistEnabled} generationMode={generationMode} setGenerationMode={setGenerationMode}
            patientName={patientName} setPatientName={setPatientName}
            modelVersion={modelVersion} setModelVersion={setModelVersion} v2OutputMode={v2OutputMode} setV2OutputMode={setV2OutputMode}
            v2CosmeticStyle={v2CosmeticStyle} setV2CosmeticStyle={setV2CosmeticStyle}
          />
        </motion.div>
      );
    }

    return (
      <motion.div key="uploader" variants={pageVariants} initial="initial" animate="in" exit="out" transition={pageTransition} className="w-full h-full">
        <ImageUploader onImageUpload={handleImageUpload} />
      </motion.div>
    );
  };

  const renderSiteContent = () => {
    return <HomePage onLaunchApp={handleLaunchApp} />;
  };

  return (
    <div className="h-screen w-full bg-zinc-100 dark:bg-zinc-950 overflow-hidden">
      {appState === 'site' ? (
        <div className="flex flex-col h-full overflow-auto">
          <Header appState={appState} onLaunchApp={handleLaunchApp} onGoHome={handleGoHome} />
          <main className="flex-grow w-full">
            <AnimatePresence mode="wait"><motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>{renderSiteContent()}</motion.div></AnimatePresence>
          </main>
        </div>
      ) : (
        <DashboardLayout>
          <AnimatePresence mode="wait">
            {isRefining && <LineLoader message={loadingMessage} />}
            {renderAppContent()}
          </AnimatePresence>

          <AnimatePresence>
            {isCroppingDenture && activeGeneratedUrl && (<ImageCropper imageUrl={activeGeneratedUrl} onCrop={handleGenerateDentalModel} onCancel={() => setIsCroppingDenture(false)} />)}
            {isCreatingMask && originalImage && extractedDentureUrl && (<MaskCreator imageUrl={originalImage.url} onComplete={handleMaskCreationComplete} onCancel={() => setIsCreatingMask(false)} />)}
            {isStudioOpen && studioFaceImageUrl && extractedDentureUrl && (<SmileStudio faceImageUrl={studioFaceImageUrl} smileModelUrl={extractedDentureUrl} onClose={() => setIsStudioOpen(false)} />)}
            {isGumLineEditorOpen && originalImage && (<GumLineEditor imageUrl={originalImage.url} onConfirm={(path) => { setGumLinePath(path); setIsGumLineEditorOpen(false); }} onCancel={() => setIsGumLineEditorOpen(false)} initialPath={gumLinePath} />)}
            {isClinicalGuideEditorOpen && originalImage && (<ClinicalGuideEditor imageUrl={originalImage.url} onConfirm={(guides) => { setClinicalGuides(guides); setIsClinicalGuideEditorOpen(false); }} onCancel={() => setIsClinicalGuideEditorOpen(false)} initialGuides={clinicalGuides} />)}
            {isRefineModalOpen && currentGeneration && originalImage && (
              <RefineModal
                isOpen={isRefineModalOpen}
                onClose={() => setIsRefineModalOpen(false)}
                onGenerate={handleGenerateFromRefineModal}
                initialOriginalImage={originalImage}
                initialGeneratedImages={currentGeneration.imageUrls}
              />
            )}
          </AnimatePresence>
          {isLoading && <Loader message={loadingMessage} />}
        </DashboardLayout>
      )}

      {/* Credit Purchase Modals */}
      <CreditPurchaseModal isOpen={showDesignPurchaseModal} onClose={() => setShowDesignPurchaseModal(false)} />
      <CreditPurchaseModal isOpen={showVideoPurchaseModal} onClose={() => setShowVideoPurchaseModal(false)} />
      <CreditPurchaseModal isOpen={showPortraitPurchaseModal} onClose={() => setShowPortraitPurchaseModal(false)} />
      <CreditPurchaseModal isOpen={showModelPurchaseModal} onClose={() => setShowModelPurchaseModal(false)} />

      {/* Validation Feedback Modal */}
      <ValidationFeedbackModal
        isOpen={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
        errors={validationErrors}
        checks={validationChecks}
      />
    </div>
  );
}

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <Suspense fallback={<LazyLoadFallback />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/mfa-setup" element={<MfaSetupPage />} />
          <Route path="/guide" element={
            <ErrorBoundary>
              <GuidePageWrapper />
            </ErrorBoundary>
          } />
          <Route path="/pricing" element={
            <ErrorBoundary>
              <PricingPageWrapper />
            </ErrorBoundary>
          } />
          <Route path="/about" element={
            <ErrorBoundary>
              <AboutUsPageWrapper />
            </ErrorBoundary>
          } />
          <Route path="/blog" element={
            <ErrorBoundary>
              <Suspense fallback={<LazyLoadFallback />}>
                <BlogHub />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/blog/:slug" element={
            <ErrorBoundary>
              <Suspense fallback={<LazyLoadFallback />}>
                <BlogPost />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/features/digital-smile-design-software-ai" element={
            <ErrorBoundary>
              <Suspense fallback={<LazyLoadFallback />}>
                <FeaturePageWrapper>
                  <DigitalSmileDesignPage />
                </FeaturePageWrapper>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/features/automated-smile-design" element={<FeaturePageWrapper><AutomatedSmileDesignPage /></FeaturePageWrapper>} />
          <Route path="/solutions/ai-in-digital-dentistry" element={<SolutionsPageWrapper><AiInDigitalDentistryPage /></SolutionsPageWrapper>} />
          <Route path="/solutions/virtual-smile-makeover" element={
            <ErrorBoundary>
              <Suspense fallback={<LazyLoadFallback />}>
                <SolutionsPageWrapper>
                  <VirtualSmileMakeoverPage />
                </SolutionsPageWrapper>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/solutions/cosmetic-dentistry-digital-tools" element={
            <ErrorBoundary>
              <Suspense fallback={<LazyLoadFallback />}>
                <SolutionsPageWrapper>
                  <CosmeticDentistryDigitalToolsPage />
                </SolutionsPageWrapper>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/solutions/ai-dental-imaging-analysis" element={
            <ErrorBoundary>
              <Suspense fallback={<LazyLoadFallback />}>
                <SolutionsPageWrapper>
                  <AiDentalImagingAnalysisPage />
                </SolutionsPageWrapper>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/solutions/smile-simulation-technology" element={<SolutionsPageWrapper><SmileSimulationTechnologyPage /></SolutionsPageWrapper>} />
          <Route path="/features/ai-powered-smile-design" element={<FeaturePageWrapper><AiPoweredSmileDesignPage /></FeaturePageWrapper>} />
          <Route path="/features/automatic-smile-design-from-photo" element={<FeaturePageWrapper><AutomaticSmileDesignFromPhotoPage /></FeaturePageWrapper>} />
          <Route path="/features/smile-design-facial-analysis" element={<FeaturePageWrapper><SmileDesignFacialAnalysisPage /></FeaturePageWrapper>} />
          <Route path="/features/ai-esthetic-dental-planning" element={<FeaturePageWrapper><AiEstheticDentalPlanningPage /></FeaturePageWrapper>} />
          <Route path="/features/real-time-smile-preview" element={<FeaturePageWrapper><RealTimeSmilePreviewPage /></FeaturePageWrapper>} />
          <Route path="/features/before-after-smile-simulation" element={<FeaturePageWrapper><BeforeAfterSmileSimulationPage /></FeaturePageWrapper>} />
          <Route path="/features/ai-smile-mockups-dentists" element={<FeaturePageWrapper><AiSmileMockupsForDentistsPage /></FeaturePageWrapper>} />
          <Route path="/features/smile-design-without-manual-drawing" element={<FeaturePageWrapper><SmileDesignWithoutManualDrawingPage /></FeaturePageWrapper>} />
          <Route path="/features/cloud-based-smile-design" element={<FeaturePageWrapper><CloudBasedSmileDesignPage /></FeaturePageWrapper>} />
          <Route path="/features/browser-based-smile-design" element={<FeaturePageWrapper><BrowserBasedSmileDesignPage /></FeaturePageWrapper>} />
          {/* Use-case pages */}
          <Route path="/use-cases/smile-design-cosmetic-dentists" element={<FeaturePageWrapper><SmileDesignCosmeticDentistsPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-design-private-clinics" element={<FeaturePageWrapper><SmileDesignPrivateClinicsPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-design-daily-consultations" element={<FeaturePageWrapper><SmileDesignDailyConsultationsPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/chairside-smile-simulation" element={<FeaturePageWrapper><ChairsideSmileSimulationPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-design-veneers" element={<FeaturePageWrapper><SmileDesignVeneersPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-design-full-mouth-rehabilitation" element={<FeaturePageWrapper><SmileDesignFullMouthRehabPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-design-minimally-invasive" element={<FeaturePageWrapper><SmileDesignMinimallyInvasivePage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-design-orthodontists" element={<FeaturePageWrapper><SmileDesignOrthodontistsPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-design-prosthodontics" element={<FeaturePageWrapper><SmileDesignProsthodonticsPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-planning-implant-cases" element={<FeaturePageWrapper><SmileDesignImplantCasesPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/smile-design-esthetic-restorations" element={<FeaturePageWrapper><SmileDesignEstheticRestorationsPage /></FeaturePageWrapper>} />
          <Route path="/use-cases/digital-wax-up-alternative" element={<FeaturePageWrapper><DigitalWaxUpAlternativePage /></FeaturePageWrapper>} />

          {/* Guide pages */}
          <Route path="/guides/reduce-smile-design-time" element={<FeaturePageWrapper><ReduceSmileDesignTimePage /></FeaturePageWrapper>} />
          <Route path="/guides/automate-digital-smile-design" element={<FeaturePageWrapper><AutomateDigitalSmileDesignPage /></FeaturePageWrapper>} />
          <Route path="/guides/alternatives-manual-smile-design" element={<FeaturePageWrapper><AlternativesManualSmileDesignPage /></FeaturePageWrapper>} />
          <Route path="/guides/traditional-smile-design-problems" element={<FeaturePageWrapper><TraditionalSmileDesignProblemsPage /></FeaturePageWrapper>} />
          <Route path="/guides/show-patients-realistic-smile-previews" element={<FeaturePageWrapper><ShowPatientsRealisticSmilePreviewsPage /></FeaturePageWrapper>} />
          <Route path="/guides/improve-patient-acceptance-smile-simulation" element={<FeaturePageWrapper><ImprovePatientAcceptanceSimulationPage /></FeaturePageWrapper>} />
          <Route path="/guides/standardize-esthetic-dental-planning" element={<FeaturePageWrapper><StandardizeEstheticDentalPlanningPage /></FeaturePageWrapper>} />
          <Route path="/guides/avoid-subjective-smile-design-decisions" element={<FeaturePageWrapper><AvoidSubjectiveSmileDesignDecisionsPage /></FeaturePageWrapper>} />
          <Route path="/guides/ai-vs-traditional-smile-design" element={<FeaturePageWrapper><AiVsTraditionalSmileDesignPage /></FeaturePageWrapper>} />
          <Route path="/guides/manual-vs-automatic-smile-design" element={<FeaturePageWrapper><ManualVsAutomaticSmileDesignPage /></FeaturePageWrapper>} />
          <Route path="/guides/digital-wax-up-vs-ai-simulation" element={<FeaturePageWrapper><DigitalWaxUpVsAiSimulationPage /></FeaturePageWrapper>} />
          <Route path="/guides/smile-design-software-vs-photoshop" element={<FeaturePageWrapper><SmileDesignSoftwareVsPhotoshopPage /></FeaturePageWrapper>} />
          <Route path="/guides/chairside-vs-lab-planning" element={<FeaturePageWrapper><ChairsideVsLabPlanningPage /></FeaturePageWrapper>} />
          <Route path="/guides/cloud-vs-desktop-smile-design" element={<FeaturePageWrapper><CloudVsDesktopSmileDesignPage /></FeaturePageWrapper>} />
          <Route element={<PrivateRoute />}>

            <Route path="/app/*" element={
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
            } />
            <Route path="/dashboard/billing" element={
              <Suspense fallback={<LazyLoadFallback />}>
                <BillingPage />
              </Suspense>
            } />
            <Route path="/dashboard" element={
              <ErrorBoundary>
                <Suspense fallback={<LazyLoadFallback />}>
                  <DashboardPage />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="/showcase" element={
              <ErrorBoundary>
                <Suspense fallback={<LazyLoadFallback />}>
                  <DashboardLayout>
                    <ShowcaseCreatorPage />
                  </DashboardLayout>
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="/admin" element={
              <ErrorBoundary>
                <Suspense fallback={<LazyLoadFallback />}>
                  <AdminDashboard />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="/admin/credits" element={
              <ErrorBoundary>
                <Suspense fallback={<LazyLoadFallback />}>
                  <AdminCreditPanel />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="/partner" element={
              <ErrorBoundary>
                <Suspense fallback={<LazyLoadFallback />}>
                  <PartnerDashboard />
                </Suspense>
              </ErrorBoundary>
            } />
            <Route path="/payment/success" element={
              <ErrorBoundary>
                <PaymentSuccess />
              </ErrorBoundary>
            } />
            <Route path="/payment/failure" element={
              <ErrorBoundary>
                <PaymentFailure />
              </ErrorBoundary>
            } />
          </Route>
          <Route path="/*" element={
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
          } />

        </Routes>
      </AnimatePresence>
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AnimatedRoutes />

          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'rgba(0, 0, 0, 0.8)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;